import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { runThirdParty } from '../../helpers/thirdparty'
import {
  pushFormSubmit,
  useFormStartWhenVisible,
  createD365FormLoadHandler,
  registerD365FormLoadListeners,
} from '../../helpers/formAnalytics'
import { registerPrefillHandler, applyPrefillToForm, registerRefreshOnSubmit } from './d365FormPrefill'
import { captureD365FormFile, uploadD365FormFile } from '../../helpers/d365FormFileUpload'
import FormSubmitOverlay from '../helpers/FormSubmitOverlay'
import { JourneyGateContext } from '../../providers/JourneyGate'

const STATUS_REASON_TO_LABEL = {
  'current-pending-approval': 'Current - Pending Approval',
  'current-pending-payment': 'Current - Pending Payment',
}

// Portal home for members who aren't eligible for the renewal journey. The renewal
// journey is purely role-gated (server computes canAccessRenewalForm), so an
// ineligible member is bounced here — mirrors App.jsx's member-area redirect style
// (window.location.href).
const MEMBER_PORTAL_HOME = '/member'

// Backstop redirect window for the fallback path where the callback returned no
// redirect target and we wait on the post-submit cache refresh instead. The happy
// path navigates straight to the Pending Approval page, so this rarely arms.
// Deliberately shorter than the full refresh poll schedule (~2 min, see
// REFRESH_POLL_DELAYS_MS) — the member shouldn't idle on the covered form that long.
const SAFETY_REDIRECT_MS = 45000

// Journey-status poll for a redirect-bound member (mirrors PendingApprovalPage). While the loading
// overlay covers the form we poll the live status endpoint and redirect the instant it resolves a
// target page. The form is NEVER uncovered into an editable state: if the poll window elapses without
// a redirect, we send the member to the portal home instead. The window is kept below JourneyGate's
// GATE_BACKSTOP_MS (15s) so the gate never opens and sibling content never flashes before navigation.
const JOURNEY_STATUS_URL = '/umbraco/surface/journey/status'
const JOURNEY_POLL_INTERVAL_MS = 1500
const JOURNEY_POLL_WINDOW_MS = 10000

// The renewal form is the correct destination only for a "Pending Renewal" status. An eligible
// member in any other pending state (e.g. Current - Pending Payment) has already renewed and is
// about to be redirected by App.checkMemberJourneyStatus, so their form must stay covered.
const statusBelongsOnRenewalForm = (status) =>
  typeof status === 'string' && status.toLowerCase().includes('pending renewal')

/** Sibling of the member-context "current" route that re-syncs the cache from D365. */
function toRefreshUrl(memberContextUrl) {
  return memberContextUrl ? memberContextUrl.replace(/\/current$/, '/refresh') : null
}

/**
 * Renders the D365 embedded single-step Renewal form (CR02).
 *
 * Replaces the previous 3-step flow. The form is identified by `formId` (RenewalSingleStepFormId
 * from D365Forms config). After submission, the component POSTs { statusReason, orderId } to
 * /umbraco/surface/renewals/callback which routes the member based on their D365 status:
 *   current-pending-payment  → invoice page
 *   current-pending-approval → pending approval page
 *   default                  → member home
 *
 * Pre-population: uses the shared d365FormPrefill helper (same mechanism as
 * MemberSelfServiceForms) — D365's FormLoader renders the form inline (NOT in an
 * iframe) and ignores data-prefill-* on the host div, so we listen for the
 * `d365mkt-afterformload` event and write values into the rendered inputs by name.
 * Member-context values are merged with the query-param fallback set by the Renew button.
 */
function RenewalsEmbedForm(props) {
  const { formApiUrl, formId, cachedFormUrl, memberContextUrl, callbackUrl, isSectionVisible = true } = props
  const [memberContext, setMemberContext] = useState(null)
  const [memberContextLoaded, setMemberContextLoaded] = useState(false)
  const [callbackError, setCallbackError] = useState(null)
  // Covers the form the instant D365 accepts the submission, keeping the member on the
  // page while the file upload + callback + redirect journey completes.
  const [submitting, setSubmitting] = useState(false)
  const containerRef = useRef(null)
  const interactionCleanupRef = useRef(null)
  const submittedRef = useRef(false)
  const capturedFileRef = useRef(null)
  // Redirect target from the callback; the post-submit refresh (or the safety backstop)
  // navigates to it once the cache reflects the just-submitted renewal.
  const redirectUrlRef = useRef(null)
  const navigatedRef = useRef(false)
  const safetyRedirectRef = useRef(null)
  // True once the post-submit refresh has fired. Guards the race where the refresh
  // completes before the callback returns the redirect target: navigation then happens
  // the moment the target is stashed, instead of waiting for the safety backstop.
  const refreshDoneRef = useRef(false)
  // Guards the eligibility redirect so it fires at most once.
  const redirectingRef = useRef(false)
  // Guards the journey-status redirect (poll target or portal-home fallback) so it fires at most once.
  // Kept separate from redirectingRef (role gate) and navigatedRef (post-submit) so the three
  // independent redirect drivers stay readable.
  const journeyRedirectedRef = useRef(false)
  const { releaseGate } = useContext(JourneyGateContext)
  const dlFormType = 'renewal'

  useEffect(() => {
    if (!memberContextUrl) {
      setMemberContextLoaded(true)
      return
    }
    let cancelled = false
    fetch(memberContextUrl, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) {
          setMemberContext(data)
          setMemberContextLoaded(true)
        }
      })
      .catch((err) => {
        console.error('[RenewalsEmbedForm] Failed to load member context:', err)
        if (!cancelled) setMemberContextLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [memberContextUrl])

  // Role gate. The renewal journey is purely gated to privileged roles: the server sets
  // canAccessRenewalForm (true only for Main/Administrator/Finance in a Pending Renewal/
  // Payment status). Once context has loaded, an ineligible member (fail-closed: unknown/
  // missing is treated as ineligible) is redirected to the portal home. Done in an effect
  // (not during render) to avoid side-effects in render; render early-returns null below.
  useEffect(() => {
    if (!memberContextLoaded) return
    if (memberContext?.canAccessRenewalForm) return
    if (redirectingRef.current) return
    redirectingRef.current = true
    window.location.href = MEMBER_PORTAL_HOME
  }, [memberContextLoaded, memberContext])

  // Clear the safety-redirect backstop on unmount so it can't fire after teardown.
  useEffect(() => () => window.clearTimeout(safetyRedirectRef.current), [])

  // When the post-submit overlay appears, scroll to the top of the page so the member
  // sees the loading spinner rather than the now-covered form they were editing.
  useEffect(() => {
    if (submitting) window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [submitting])

  // Journey-status poll. An eligible but redirect-bound member (e.g. transitioning Current - Pending
  // Approval → Current - Pending Payment) is covered by the loading overlay and the form embed is never
  // mounted (see render). We poll the live status endpoint and redirect the instant it resolves a target
  // page — so the member is routed to the pending-approval or invoice page without ever seeing the form.
  // If the window elapses without a redirect, we send them to the portal home (never uncover the form).
  useEffect(() => {
    if (!memberContextLoaded || !memberContext?.canAccessRenewalForm) return
    if (statusBelongsOnRenewalForm(memberContext?.status)) return

    let cancelled = false
    let timerId
    const here = window.location.pathname.replace(/\/$/, '')
    const deadline = Date.now() + JOURNEY_POLL_WINDOW_MS

    const redirectOnce = (url) => {
      if (journeyRedirectedRef.current || !url) return
      journeyRedirectedRef.current = true
      window.location.href = url
    }

    const scheduleNext = () => {
      if (Date.now() < deadline) {
        timerId = window.setTimeout(poll, JOURNEY_POLL_INTERVAL_MS)
      } else if (!cancelled) {
        // No target resolved in time — route to the portal home rather than uncovering the form.
        redirectOnce(MEMBER_PORTAL_HOME)
      }
    }

    const poll = () => {
      fetch(JOURNEY_STATUS_URL, { credentials: 'same-origin' })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (cancelled) return
          const target = (data?.redirectUrl || '').replace(/\/$/, '')
          if (target && target !== here) {
            redirectOnce(data.redirectUrl)
            return
          }
          scheduleNext()
        })
        .catch(() => {
          if (!cancelled) scheduleNext()
        })
    }

    // First poll after one interval — give D365 a moment to reflect the flipped status.
    timerId = window.setTimeout(poll, JOURNEY_POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      if (timerId) window.clearTimeout(timerId)
    }
  }, [memberContextLoaded, memberContext])

  // Open the journey gate only once we know this member is staying on the form: context loaded,
  // role eligible, and a genuine "Pending Renewal" status. Ineligible/redirect-bound members stay
  // gated until navigation replaces the page — the journey-status poll above always drives a redirect
  // (target page or portal-home fallback) well before the provider's GATE_BACKSTOP_MS, so the gate
  // never opens and sibling content never flashes.
  useEffect(() => {
    if (!memberContextLoaded || !memberContext?.canAccessRenewalForm) return
    if (!statusBelongsOnRenewalForm(memberContext?.status)) return
    releaseGate()
  }, [memberContextLoaded, memberContext, releaseGate])

  const uploadCapturedFile = () => uploadD365FormFile(capturedFileRef.current, 'RenewalsEmbedForm')

  // Navigates once to the stashed redirect target. Idempotent so the post-submit refresh
  // and the safety backstop can both call it without double-navigating.
  const navigateOnce = useCallback((url) => {
    if (navigatedRef.current || !url) return
    navigatedRef.current = true
    window.location.href = url
  }, [])

  // Fired by registerRefreshOnSubmit once D365 reflects the renewal (the cached snapshot
  // changed vs the pre-submit baseline) — route the member to the callback's redirect.
  const handleRefreshed = useCallback(() => {
    refreshDoneRef.current = true
    navigateOnce(redirectUrlRef.current)
  }, [navigateOnce])

  // Covers the form the instant D365 accepts the submission (same overlay onRenewalSubmitted
  // shows), keeping the member on the page while the refresh + redirect completes.
  const handleSubmitStart = useCallback(() => {
    setSubmitting(true)
  }, [])

  // Shared submit handler. Fires once: uploads the captured financial statement (before routing so
  // the redirect can't cancel it), then posts the renewal callback and routes the member.
  const onRenewalSubmitted = useCallback(async (statusReason) => {
    if (submittedRef.current) return
    submittedRef.current = true
    setSubmitting(true)

    pushFormSubmit(dlFormType)

    const hint = STATUS_REASON_TO_LABEL[statusReason?.toLowerCase?.()]
    if (hint) sessionStorage.setItem('nds_member_status_hint', hint)

    await uploadCapturedFile()

    setCallbackError(null)
    if (!callbackUrl) {
      setCallbackError('Unfortunately, something went wrong while processing your renewal. Please contact the NDS Membership Team at membership@nds.org.au so we can investigate the issue and complete your renewal.')
      setSubmitting(false)
      return
    }
    try {
      const res = await fetch(callbackUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ statusReason: statusReason ?? null, orderId: null }),
      })
      if (!res.ok) throw new Error(String(res.status))
      const result = await res.json()
      redirectUrlRef.current = result.redirectUrl ?? null
      // The Pending Approval page now owns the post-submit wait — it polls live D365 for the flip to
      // Current - Pending Payment (up to ~3 min) and routes to the invoice page, or reveals its own
      // content if the status stays Pending Approval. So go straight there instead of waiting on the
      // cache-refresh backoff: the destination reads live status, not the Umbraco cache. Avoids a
      // double wait (form here + page).
      if (redirectUrlRef.current) {
        navigateOnce(redirectUrlRef.current)
        return
      }
      // No target returned (shouldn't happen — the callback always returns the approval page URL):
      // fall back to the cache-refresh signal / safety backstop so the member is never stranded on
      // the overlay.
      if (refreshDoneRef.current) {
        navigateOnce(redirectUrlRef.current)
        return
      }
      window.clearTimeout(safetyRedirectRef.current)
      safetyRedirectRef.current = window.setTimeout(
        () => navigateOnce(redirectUrlRef.current),
        SAFETY_REDIRECT_MS,
      )
    } catch (err) {
      console.error('[RenewalsEmbedForm] Callback failed:', err)
        setCallbackError('Unfortunately, something went wrong while processing your renewal. Please contact the NDS Membership Team at membership@nds.org.au so we can investigate the issue and complete your renewal.')
      setSubmitting(false)
    }
  }, [callbackUrl, navigateOnce])

  // Capture the chosen file DURING submit (d365mkt-formsubmit), while the form inputs still exist —
  // the form swaps to its confirmation view afterwards and the inputs disappear.
  const captureFile = useCallback((event) => {
    const formEl = event?.detail?.formElement ?? event?.target ?? containerRef.current
    const file = captureD365FormFile(formEl)
    if (file) capturedFileRef.current = file
  }, [])

  // Path 1 — iframe embeds postMessage a { type: 'formSubmitted', statusReason } message.
  const handleMessage = useCallback(
    (event) => {
      const data = event.data
      if (!data) return
      if (data.type !== 'formSubmitted' && data.formSubmitted !== true) return
      onRenewalSubmitted(data.statusReason ?? data.StatusReason ?? null)
    },
    [onRenewalSubmitted],
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [handleMessage])

  // Path 2 — the events the INLINE D365 form actually fires. FormLoader dispatches d365mkt-* events
  // on window.d365mktforms / document (bubbling to document), NOT a postMessage — the same mechanism
  // the registration form uses. formsubmit fires while inputs still exist (capture the file);
  // afterformsubmit fires once submission succeeds (upload + route).
  useEffect(() => {
    const onAfterSubmit = (event) => {
      const detail = event?.detail || {}
      if (detail.isSuccess === false) return
      const statusReason = extractStatusReason(detail)
      // Logged so we can confirm what D365 returns for the fee-type outcome on submit.
      console.debug('[RenewalsEmbedForm] afterformsubmit statusReason:', statusReason, detail)
      onRenewalSubmitted(statusReason)
    }
    const api = typeof window !== 'undefined' ? window.d365mktforms : null
    const targets = [api, typeof document !== 'undefined' ? document : null].filter(Boolean)
    targets.forEach((t) => {
      t.addEventListener?.('d365mkt-formsubmit', captureFile)
      t.addEventListener?.('d365mkt-afterformsubmit', onAfterSubmit)
    })
    return () => {
      targets.forEach((t) => {
        t.removeEventListener?.('d365mkt-formsubmit', captureFile)
        t.removeEventListener?.('d365mkt-afterformsubmit', onAfterSubmit)
      })
    }
  }, [onRenewalSubmitted, captureFile])

  useFormStartWhenVisible(dlFormType, {
    ready: !!(formId && memberContextLoaded),
    isSectionVisible,
  })

  useEffect(() => {
    const handleFormLoad = createD365FormLoadHandler(containerRef, interactionCleanupRef)
    const unregister = registerD365FormLoadListeners(handleFormLoad)

    return () => {
      unregister()
      interactionCleanupRef.current?.()
      interactionCleanupRef.current = null
    }
  }, [formId])

  useEffect(() => {
    if (!memberContextLoaded || !formId || !containerRef.current) return

    // A redirect-bound member's embed is never mounted (see render) and they are always routed away
    // by the journey-status poll, so there is nothing to prefill/register — bail out. Only a genuine
    // "Pending Renewal" member reaches the prefill/register work below.
    const resuming = !statusBelongsOnRenewalForm(memberContext?.status)
    if (resuming) return

    const formEl = containerRef.current.querySelector('[data-form-id]')
    if (formEl) {
      delete formEl.dataset.processed
    }

    // Merge the API member context with the query-param fallback set by the Renew
    // button URL, then prefill via the shared d365FormPrefill helper.
    const prefillContext = mergeQueryPrefill(memberContext)

    // Register the prefill listener BEFORE FormLoader.bundle.js fires
    // d365mkt-afterformload. If the bundle is already loaded the listener attaches
    // now; otherwise registerPrefillHandler is a no-op until the global appears and
    // we re-register inside the retry loop below.
    registerPrefillHandler(prefillContext)

    // After submit, re-sync the Umbraco cache from D365 (polling with backoff so we
    // capture D365's async ingest of the renewal, not the pre-submit snapshot), then
    // redirect. Baseline is the raw /current context (not the merged prefill) so change
    // detection compares against the server snapshot. Unlike MemberSelfServiceForms this
    // does NOT reload — handleRefreshed routes to the callback's redirect via navigateOnce.
    const refreshUrl = toRefreshUrl(memberContextUrl)
    registerRefreshOnSubmit(refreshUrl, handleRefreshed, memberContext, handleSubmitStart)

    runThirdParty()

    // FormLoader.bundle.js is async; on cached loads the form can render before our
    // listener attaches. Re-register (idempotent) and apply prefill manually for any
    // form already in the DOM. Bounded retry guards against the bundle never loading.
    let attempts = 0
    const retryId = window.setInterval(() => {
      attempts += 1
      registerPrefillHandler(prefillContext)
      registerRefreshOnSubmit(refreshUrl, handleRefreshed, memberContext, handleSubmitStart)
      containerRef.current?.querySelectorAll('form').forEach((form) => {
        applyPrefillToForm(form, prefillContext)
      })
      if (attempts >= 10) window.clearInterval(retryId)
    }, 500)

    return () => window.clearInterval(retryId)
  }, [formId, memberContextLoaded, memberContext, memberContextUrl, handleRefreshed, handleSubmitStart])

  if (!formApiUrl || !formId) return null
  if (!memberContextLoaded) return null
  // Role gate (fail-closed): ineligible members never see the form — the effect above
  // redirects them to the portal home; render nothing while that redirect is in flight.
  if (!memberContext?.canAccessRenewalForm) return null

  // A redirect-bound member (eligible but not in a "Pending Renewal" state) is being routed away by
  // the journey-status poll above — cover the form with the loading overlay and never mount the embed,
  // so they can never start editing a form they should not see. Stays covered until navigation.
  const journeyResuming = !statusBelongsOnRenewalForm(memberContext?.status)
  // While either covering state is active the embed is hidden and the ~600px spinner cover shows
  // in its place, so the loading spinner is clearly visible (never clipped by the embed) and the
  // rest of the page stays visible.
  const coverForm = submitting || journeyResuming

  return (
    <div className="renewals-embed-form px-4 sm:px-8 lg:px-12">
      {callbackError && (
        <div className="renewals-embed-form__error" role="alert">
          {callbackError}
        </div>
      )}

      <div
        ref={containerRef}
        className={`relative ${coverForm ? 'min-h-[600px]' : ''}`}
        aria-busy={coverForm}
      >
        {/* journeyResuming: don't mount the embed at all — the form never loads for a redirect-bound
            member. submit: keep the (already-loaded) embed mounted but hidden so the clean cover
            shows in its place; it returns if the cover clears without a redirect (e.g. callback error). */}
        {!journeyResuming && (
          <div
            className={submitting ? 'hidden' : undefined}
            data-form-id={formId}
            data-form-api-url={formApiUrl}
            data-cached-form-url={cachedFormUrl}
          />
        )}
        {coverForm ? (
          <FormSubmitOverlay
            className="rounded-3xl rounded-tl-none"
            message={journeyResuming ? 'Loading…' : undefined}
          />
        ) : null}
      </div>
    </div>
  )
}

RenewalsEmbedForm.propTypes = {
  formApiUrl: PropTypes.string,
  formId: PropTypes.string,
  cachedFormUrl: PropTypes.string,
  memberContextUrl: PropTypes.string,
  callbackUrl: PropTypes.string,
  isSectionVisible: PropTypes.bool,
}

/**
 * Pulls D365's fee-type outcome (statusReason) out of the d365mkt-afterformsubmit event detail.
 * D365 returns "current-pending-payment" (no fee change) or "current-pending-approval" (fee change).
 * The exact shape isn't documented, so we check the common top-level keys and one level down.
 */
function extractStatusReason(detail) {
  if (!detail || typeof detail !== 'object') return null
  const pick = (o) => o?.statusReason ?? o?.StatusReason ?? o?.status ?? null
  const direct = pick(detail)
  if (direct) return direct
  for (const v of Object.values(detail)) {
    if (v && typeof v === 'object') {
      const nested = pick(v)
      if (nested) return nested
    }
  }
  return null
}

/**
 * Merges the API member context with the query-param fallback set by the Renew
 * button URL. The query params are mapped onto the same context keys that
 * d365FormPrefill's FIELD_NAME_MAP expects; the API context wins where present.
 */
function mergeQueryPrefill(ctx) {
  const merged = { ...(ctx || {}) }
  if (typeof window === 'undefined') return merged

  // Renewal sheet marks these as "Pre Population Not Required" — strip so the
  // member enters them manually on the renewal form.
  //
  // NOTE: `email` is intentionally NOT stripped. D365 requires emailaddress1 (the contact's
  // login email) on the renewal submission to identify/update the contact, so it must be
  // prefilled. Only the visible "email of person completing this form"
  // (EmailOfPersonCompletingTheForm) stays manual — and it is untouched here because it is
  // not in d365FormPrefill's FIELD_NAME_MAP (which only targets emailaddress1/emailaddress2/email).
  delete merged.totalAnnualIncome
  delete merged.disabilityIncome

  const p = new URLSearchParams(window.location.search)
  const fallback = (key, param) => {
    const current = merged[key]
    const value = p.get(param)
    if ((current == null || current === '') && value) {
      merged[key] = value
    }
  }
  fallback('contactId', 'contactId')
  fallback('providerId', 'providerId')
  fallback('firstName', 'fname')
  fallback('lastName', 'lname')
  // No email query-param fallback: emailaddress1 is prefilled from the API member context
  // (merged.email above). The visible "email of person completing this form" stays manual.
  fallback('mobile', 'mobile')
  fallback('positionTitle', 'position')
  fallback('state', 'state')
  return merged
}

export default RenewalsEmbedForm
